/**
 * ============================================================================
 * useTransactionsData - LEDGER-BASED TRANSACTIONS HOOK
 * ============================================================================
 *
 * ⚠️  CRITICAL: THIS NOW USES THE PAYMENT LEDGER API
 *
 * The PaymentLedger provides:
 * - Immutable transaction records (can't be changed after creation)
 * - running_balance stored at transaction time (real "Balance After")
 * - All financial movements: payments, refunds, payouts, debts
 *
 * The "Balance" column now shows the ACTUAL balance after each transaction,
 * stored permanently at transaction time - not calculated dynamically.
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from '../utils/currency';
import { useQuery } from '@tanstack/react-query';
import { vendorApi } from '../services/api';
import type { Transaction, TransactionsStats, TransactionsFilters } from '../types/transactions';

interface TransactionsDataResponse {
  transactions: Transaction[];
  stats: TransactionsStats;
  totalCount: number;
}

/**
 * Fetches ledger-based transaction history
 * Uses the PaymentLedger for accurate, immutable financial records
 */
export function useTransactionsData(
  venueId: number | undefined,
  filters: TransactionsFilters
) {
  return useQuery<TransactionsDataResponse, Error>({
    queryKey: ['ledger-transactions', venueId, filters.type, filters.dateRange],

    queryFn: async () => {
      if (!venueId) throw new Error('Venue ID is required');

      // Map filter types to ledger parameters
      // booking_type shows payments AND their refunds (for "One-time", "Recurring", "Membership")
      // type shows only that entry type (for "Refunds", "Payouts")
      const bookingTypeMap: Record<string, string> = {
        'booking': 'payment',
        'recurring': 'recurring_booking_payment',
        'membership': 'gym_membership_payment',
      };

      const entryTypeMap: Record<string, string> = {
        'refund': 'refund',
        'payout': 'payout',
        'debt': 'debt',
      };

      // Build API params
      const params: {
        page?: number;
        per_page?: number;
        type?: string;
        booking_type?: string;
        days?: number;
      } = {
        per_page: 500,
      };

      // Apply filter - use booking_type for booking categories (includes refunds)
      // Use type for entry-specific filters (refunds only, payouts only)
      if (filters.type !== 'all') {
        if (bookingTypeMap[filters.type]) {
          // "One-time", "Recurring", "Membership" → show payments + their refunds
          params.booking_type = bookingTypeMap[filters.type];
        } else if (entryTypeMap[filters.type]) {
          // "Refunds", "Payouts", "Debt" → show only that entry type
          params.type = entryTypeMap[filters.type];
        }
      }

      // Apply date filter (0 = all time)
      if (filters.dateRange !== 'all' && typeof filters.dateRange === 'number' && filters.dateRange > 0) {
        params.days = filters.dateRange;
      } else {
        params.days = 0; // All time
      }

      // Fetch from ledger API
      const response = await vendorApi.getLedgerEntries(venueId, params);

      // Transform using shared function (also used by prefetch)
      return transformLedgerResponse(response);
    },

    enabled: !!venueId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
  });
}

/**
 * Transform raw ledger API response into TransactionsDataResponse
 * Used by both useTransactionsData hook and usePrefetch to ensure identical data shape
 */
export function transformLedgerResponse(response: any): TransactionsDataResponse {
  // Transform ledger entries to Transaction format
  const transactions: Transaction[] = response.data.map((entry: any) => ({
    id: entry.id.toString(),
    type: mapLedgerTypeToTransactionType(entry.entry_type),
    description: entry.description,
    amount: entry.amount,
    is_credit: entry.is_credit,
    balance: entry.running_balance,
    formatted_balance: `${CURRENCY_SYMBOL}${entry.running_balance.toLocaleString('en-NG', { minimumFractionDigits: 2 })}`,
    is_split_payment: entry.metadata?.is_split_payment === true,
    status: mapLedgerStatusToStatus(entry.entry_type, entry.is_credit),
    date: entry.recorded_at,
    customer: entry.customer_name || undefined,
    booking_reference: entry.booking_reference || undefined,
    formatted_date: entry.recorded_at_formatted,
    type_label: entry.type_label,
    status_label: entry.type_label,
  }));

  // Use summary from API (calculated correctly on backend)
  const stats: TransactionsStats = {
    grossRevenue: response.summary.total_credits,
    totalPayouts: 0,
    totalRefunds: 0,
    currentBalance: response.summary.current_balance,
  };

  // Calculate payouts and refunds from transactions
  transactions.forEach(t => {
    if (t.type === 'payout' && !t.is_credit) {
      stats.totalPayouts += t.amount;
    }
    if (t.type === 'refund' && !t.is_credit) {
      stats.totalRefunds += t.amount;
    }
  });

  return {
    transactions,
    stats,
    totalCount: response.meta.total,
  };
}

/**
 * Map ledger entry_type to Transaction type for UI display
 */
function mapLedgerTypeToTransactionType(entryType: string): 'booking' | 'recurring' | 'membership' | 'refund' | 'payout' | 'debt' {
  switch (entryType) {
    case 'payment':
      return 'booking';
    case 'recurring_booking_payment':
      return 'recurring';
    case 'gym_membership_payment':
      return 'membership';
    case 'refund':
      return 'refund';
    case 'payout':
      return 'payout';
    case 'debt':
    case 'debt_clearance':
      return 'debt';
    default:
      return 'booking';
  }
}

/**
 * Map ledger entry to a display status
 */
function mapLedgerStatusToStatus(entryType: string, isCredit: boolean): string {
  if (entryType === 'payout') return 'processed';
  if (entryType === 'refund') return 'refunded';
  if (entryType === 'debt') return isCredit ? 'cleared' : 'pending';
  if (entryType === 'debt_clearance') return 'cleared';
  return 'completed';
}
